import moment, { unitOfTime } from 'moment';
import { ICustomMilestone } from '@/types/order';
import { IOrganizationItem } from '@/types/organization';
import { IUserItem } from '@/types/user';
import { IGroupedPrice } from '@utils/index';

// Defines period types that can be associated with different aspects of a plan or offer.
export enum ChoicesPeriodType {
  PERIOD_TYPE_UNSPECIFIED = 'PERIOD_TYPE_UNSPECIFIED', // Default value when no period type is specified.
  DAY = 'DAY', // Represents a daily period.
  MONTH = 'MONTH', // Represents a monthly period.
  YEAR = 'YEAR', // Represents an annual period.
}

export enum ChoicesOrderUnitMeasure {
  quantity = 'quantity',
  percentageAmount = 'percentageAmount',
  amount = 'amount',
}

export enum ChoicesTypePlan {
  unspecified = 'unspecified',
  free = 'free',
  trial = 'trial',
  grace = 'grace',
  commitment = 'commitment',
  flexible = 'flexible',
  offline = 'offline',
  spot = 'spot',
}

// Enumerates potential payment conditions.
export enum ChoicesPaymentConditionPlan {
  UNSPECIFIED = 'unspecified', // No specific payment condition specified.
  prepay = 'prepay', // Requires payment before service is rendered.
  postpay = 'postpay', // Allows payment after service is rendered.
}

// Describes how frequently billing occurs.
export enum ChoicesBillingFrequencyPlan {
  UNSPECIFIED = 'unspecified', // No specific frequency specified.
  annually = 'annually', // Billing once a year.
  monthly = 'monthly', // Billing once a month.
  triennial = 'triennial', // Billing once every three years.
}

// Specifies types of operations that can be applied to an order item.
export enum ChoicesOrderItemType {
  newResource = 'newResource', // Represents a new subscription.
  upgrade = 'upgrade', // Represents upgrading an existing subscription.
  addLicenses = 'addLicenses', // Represents adding additional licenses.
  renewal = 'renewal', // Represents renewing an existing subscription.
}

// Defines the types of order items that are relevant for billing processes.
export const ChoicesOrderItemTypeForBilling = {
  ...ChoicesOrderItemType,
  billingCloud: 'billingCloud', // Special type for cloud consumption items.
} as const;

// Represents the structure of an order including categorized items based on their action types.
export interface IOrderItems {
  items: IOrderItem[];
  itemsToRenewal: IOrderItem[];
  itemsToUpgrade: IOrderItem[];
  itemsToAddLicenses: IOrderItem[];
}

// Defines the structure for pricing details.
export interface IPrice {
  price: number; // Base price before adjustments.
  costPrice: number; // Cost price to the provider.
  discount: number; // Discount amount applied.
  finalPrice: number; // Final price after all adjustments.
  margin: number; // Profit margin.
}

// Describes a pricing tier within a price phase.
export interface IPriceTier {
  firstResource: number; // Starting resource count for this tier.
  lastResource: number; // Ending resource count for this tier, 0 if open-ended.
  price: IPrice; // Associated price details.
}

// Represents a pricing phase which may contain multiple tiers.
export interface PricePhase {
  firstPeriod: number; // Start period of the phase.
  lastPeriod: number; // End period of the phase, 0 if ongoing.
  periodType: ChoicesPeriodType; // Type of period.
  hasTiers: boolean; // Indicates if the phase includes tiers.
  priceTiers: IPriceTier[]; // Array of price tiers.
  price: IPrice; // Default price if no tiers.
}

export interface IExtendedPriceTier extends Omit<IPriceTier, 'price'> {
  price: {
    finalPrice?: number;
    costPrice?: number;
    margin?: number;
  };
}
export interface IPhaseGroup extends Omit<IGroupedPrice, 'price' | 'priceTiers'> {
  firstPeriod?: number;
  lastPeriod?: number;
  price?: {
    finalPrice?: number;
    costPrice?: number;
    margin?: number;
  };
  priceTiers?: IExtendedPriceTier[];
}

// Details custom pricing, possibly involving multiple phases.
export interface ICustomPrice {
  price: IPrice; // Base price if no phases are defined.
  hasPhases: boolean; // Indicates if there are multiple phases.
  pricePhases: PricePhase[]; // Array of price phases.
}

// Describes an offer associated with an order item.
export interface IOffer {
  name: string; // Name of the offer.
  offerType: string; // Type of the offer.
  resourceType: string; // Resource type involved.
  hasPhases: boolean; // Indicates if the offer includes phases.
  pricePhases: PricePhase[]; // Price phases if applicable.
  endTime?: string; // Optional end time of the offer.
  startTime?: string; // Optional start time of the offer.
  price: IPrice; // Optional base price of the offer.
}

// Defines conditions and terms for a plan.
export interface ITemCondition {
  code: string; // Identifier code for the condition.
  amount: number; // Amount associated with the term.
  unit: unitOfTime.DurationConstructor; // Unit of the term amount.
  customerTermCondition?: string;
  customerBillingFrequency?: ChoicesBillingFrequencyPlan;
}

// Represents a plan which can be associated with a subscription.
export interface IPlan {
  _id: string; // Unique identifier for the plan.
  name: string; // Name of the plan.
  catalogName: string; // Name as it appears in catalog listings.
  type: string; // Type of the plan.
  splitTerm: boolean; // Whether the term can be split.
  customerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the customer.
  providerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the provider.
  paymentCondition: ChoicesPaymentConditionPlan; // Payment condition.
  customerTermCondition: ITemCondition; // Customer-specific terms.
  providerTermCondition: ITemCondition; // Provider-specific terms.
  listPrice?: number; // Optional listed price.
  listCost?: number; // Optional listed cost.
  listMargin?: number; // Optional listed margin.
}

// Details a subscription, potentially including custom pricing and terms.
export interface ISubscription {
  _id: string; // Unique identifier for the subscription.
  plan: IPlan; // Associated plan.
  offer: IOffer; // Associated offer.
  customerTermCondition: ITemCondition; // Customer-specific terms.
  customerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the customer.
  customTerm: ITemCondition; // Custom terms for the subscription.
  provisionDate: moment.Moment; // Start date of the subscription.
  renewalDate: string; // Standard end date of the subscription.
  renewalDateOperational: string; // Operational end date used for processing.

  usedQuantity: number; // Quantity of the subscription that's been used.
  purchaseQuantity: number; // Quantity of the subscription that's been purchased.

  hasPhases: boolean; // Indicates if there are multiple pricing phases.
  pricePhases: PricePhase[]; // Price phases if applicable.
  hasCustomPrice: boolean; // Indicates if there is custom pricing.
  customPrice: ICustomPrice; // Custom pricing details.
  price?: IPrice; // Standard price if no custom pricing.
  subscription: any; // Associated subscription if exists.
  organization: IOrganizationItem;
}

// Describes a billing milestone within a subscription or order item.
export interface IBillingMilestone {
  date: string; // Date of the milestone.
  period: number; // Period associated with this milestone.
  price: number; // Price at this milestone.
  quantity: number; // Quantity associated with this milestone.
  type: string; // Type of milestone.
  description: string; // Description of the milestone.
}

// Represents an order item within a larger order.
export interface IOrderItem {
  _id: string; // Unique identifier for the item.
  tempId: string; // Temporary identifier for processing.
  // Unused variables
  organization: any;
  partner: any;
  domain: any;
  provider: any;
  product: any;
  sku: any;
  payFromBag: boolean;
  settingType: string;
  additionalDocuments: any;
  maintainPrevious: any;
  autoRenewal: any;
  order: any;
  // End of unused variables

  orderItemType: ChoicesOrderItemType; // Type of the order item.
  quantity: number; // Quantity after processing.
  customPrice: ICustomPrice; // Custom pricing details.
  offer: IOffer; // Associated offer.
  plan: IPlan; // Associated plan.

  provisionDate: moment.Moment; // Provision date for the item.
  billingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency.
  paymentCondition: ChoicesPaymentConditionPlan; // Payment condition.
  billingMilestones: IBillingMilestone[]; // Array of billing milestones.
  oldSubscription: ISubscription;
  subscription: ISubscription; // Associated subscription if exists.

  customizeBillingMilestones: boolean;
  numberOfMilestones: number;
  customMilestones: ICustomMilestone[];
  finalCustomMilestones: IMilestone[];

  approvalResponsibles?: IUserItem[];
  provisionResponsibles?: IUserItem[];

  summary: any;
  syncErrorMessage?: string;
}

// Represents the state structure used within a specific context, managing order items.
export interface OrderState {
  orderItems: IOrderItem[]; // Array of order items managed by the state.
}

export enum BillingCycleType {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

export enum PaymentType {
  INITIAL = 'INITIAL',
  COMMITTED = 'COMMITTED',
  ONETIME = 'ONE-TIME',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export enum PlanType {
  COMMITMENT = 'commitment',
  TRIAL = 'trial',
  OFFLINE = 'offline',
}

export interface Payments {
  paymentPeriod: number;
  paymentDate: moment.Moment;
  startPeriod: moment.Moment;
  endPeriod: moment.Moment;
  proportionalDays: number;

  price: number; // Base price before adjustments.
  costPrice: number; // Cost price to the provider.
  discount: number; // Discount amount applied.
  finalPrice: number; // Final price after all adjustments.
  margin: number; // Profit margin.
  amount: number;

  subtotal: number;
  totalDiscount: number;
  total: number;

  type: PaymentType;
  status: PaymentStatus;
  // paymentPrice: number;
}

export interface IOrderItemSummary {
  orderItemType: ChoicesOrderItemType;
  provisionDate: string;
  provisionToday: boolean;
  provision: IProvision;
  subscription: ISummarySubscription;
}

export interface ISummarySubscription {
  quantity: number;
}

export interface IProvision {
  quantity: number;
  planType: ChoicesTypePlan;
  termCondition: ITermCondition;
  billingFrequency: ChoicesBillingFrequencyPlan;
  startDate: moment.Moment;
  endDate: moment.Moment;
  endBillingCicle: moment.Moment;
  period: number;
  payments: IPayments[];
  milestones: IMilestone[];
}

export interface ITermCondition {
  _id: string;
  name: string;
  code: string;
  amount: number;
  unit: moment.unitOfTime.DurationConstructor;
}

export interface IMilestone {
  _id?: string;
  paymentDate: moment.Moment;
  milestonePeriod: number;
  payments: IPayments[];
  type: PaymentType;
  totalDiscount: number;
  subtotal: number;
  total: number;
  startPeriod: Date;
  endPeriod: Date;
}

export interface IPayments {
  amount: number;
  costPrice: number;
  discount: number;
  endPeriod: moment.Moment;
  finalPrice: number;
  margin: number;
  paymentDate: moment.Moment;
  paymentPeriod: number;
  price: number;
  proportionalDays: number;
  startPeriod: moment.Moment;
  status: PaymentStatus;
  subtotal: number;
  total: number;
  totalDiscount: number;
  type: PaymentType;
}
